import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { siteData } from '../data/mockData';
import { ArrowRight, ArrowUp, SlidersHorizontal, X } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useTranslation } from '../data/translations';

const ProjectCard = ({ project, index, viewLabel }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const cardRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.15 }
    );
    if (cardRef.current) observer.observe(cardRef.current);
    return () => observer.disconnect();
  }, []);

  const isEven = index % 2 === 0;

  return (
    <div
      ref={cardRef}
      className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center py-12 md:py-16"
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(50px)',
        transition: 'all 0.8s cubic-bezier(0.16, 1, 0.3, 1)',
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Text Content */}
      <div className={`${isEven ? 'lg:order-1' : 'lg:order-2'}`}>
        <h3 className="text-3xl md:text-4xl lg:text-5xl font-black text-white uppercase tracking-tight mb-4 italic">
          {project.title}
        </h3>
        <p className="text-gray-400 text-base md:text-lg leading-relaxed mb-6 max-w-lg">
          {project.description}
        </p>
        <button
          className={`px-7 py-3 text-sm font-bold tracking-[0.15em] uppercase transition-all duration-500 cursor-pointer ${
            isEven
              ? 'border-2 text-white hover:bg-[#A855F7] hover:border-[#A855F7]'
              : 'bg-[#A855F7] text-white hover:bg-[#9333EA]'
          }`}
          style={isEven ? { borderColor: '#A855F7' } : {}}
          onClick={() => navigate(`/projecten/${project.slug}`)}
        >
          {viewLabel}
        </button>
      </div>

      {/* Image with purple frame */}
      <div
        className={`relative ${isEven ? 'lg:order-2' : 'lg:order-1'} cursor-pointer`}
        onClick={() => navigate(`/projecten/${project.slug}`)}
      >
        <div
          className="relative overflow-hidden"
          style={{
            transform: isHovered ? 'perspective(800px) rotateY(0deg) scale(1.02)' : `perspective(800px) rotateY(${isEven ? '-2' : '2'}deg)`,
            transition: 'transform 0.6s cubic-bezier(0.16, 1, 0.3, 1)',
          }}
        >
          {/* Purple frame border */}
          <div
            className="absolute inset-0 z-10 pointer-events-none"
            style={{
              border: '3px solid #A855F7',
              boxShadow: isHovered
                ? '0 0 30px rgba(168, 85, 247, 0.4), inset 0 0 30px rgba(168, 85, 247, 0.1)'
                : '0 0 15px rgba(168, 85, 247, 0.2)',
              transition: 'box-shadow 0.5s ease',
            }}
          />
          <img
            src={project.image}
            alt={project.title}
            className="w-full aspect-[4/3] object-cover transition-transform duration-700"
            style={{
              transform: isHovered ? 'scale(1.05)' : 'scale(1)',
            }}
          />
        </div>
      </div>
    </div>
  );
};

const FilterPanel = ({ isOpen, onClose, filters, activeFilters, onToggleFilter, onApply, onClear, t, get }) => {
  return (
    <div
      className={`fixed inset-0 z-[70] transition-all duration-500 ${
        isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
      }`}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />

      {/* Panel */}
      <div
        className={`absolute right-0 top-0 h-full w-full max-w-md bg-[#111] p-8 md:p-12 overflow-y-auto transition-transform duration-500 ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between mb-10">
          <h3 className="text-2xl font-bold text-white">{get(t.projectsPage.filters)}</h3>
          <button onClick={onClose} className="text-white hover:text-[#A855F7] transition-colors">
            <X size={28} />
          </button>
        </div>

        {/* Voor wie? */}
        <div className="mb-8">
          <h4 className="text-sm font-bold tracking-[0.15em] uppercase mb-4" style={{ color: '#A855F7' }}>
            {get(t.projectsPage.forWhom)}
          </h4>
          <div className="flex flex-wrap gap-2">
            {filters.voorWie.map((f) => (
              <button
                key={f}
                onClick={() => onToggleFilter('voorWie', f)}
                className={`px-4 py-2 text-sm font-medium transition-all duration-300 border ${
                  activeFilters.voorWie.includes(f)
                    ? 'bg-[#A855F7] border-[#A855F7] text-white'
                    : 'border-gray-600 text-gray-300 hover:border-[#A855F7] hover:text-white'
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>

        {/* Wat? */}
        <div className="mb-10">
          <h4 className="text-sm font-bold tracking-[0.15em] uppercase mb-4" style={{ color: '#A855F7' }}>
            {get(t.projectsPage.what)}
          </h4>
          <div className="flex flex-wrap gap-2">
            {filters.wat.map((f) => (
              <button
                key={f}
                onClick={() => onToggleFilter('wat', f)}
                className={`px-4 py-2 text-sm font-medium transition-all duration-300 border ${
                  activeFilters.wat.includes(f)
                    ? 'bg-[#A855F7] border-[#A855F7] text-white'
                    : 'border-gray-600 text-gray-300 hover:border-[#A855F7] hover:text-white'
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>

        <div className="flex gap-3">
          <button
            onClick={onApply}
            className="flex-1 py-3 text-sm font-bold tracking-[0.15em] uppercase transition-all duration-500 text-white"
            style={{ backgroundColor: '#A855F7' }}
          >
            {get(t.projectsPage.apply)}
          </button>
          <button
            onClick={onClear}
            className="px-6 py-3 text-sm font-bold tracking-[0.15em] uppercase border border-gray-600 text-gray-300 hover:border-white hover:text-white transition-all duration-300"
          >
            {get(t.projectsPage.clear)}
          </button>
        </div>
      </div>
    </div>
  );
};

const ProjectsPage = () => {
  const { lang } = useLanguage();
  const { t, get } = useTranslation(lang);
  const navigate = useNavigate();
  const [filterOpen, setFilterOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState({ voorWie: [], wat: [] });
  const [appliedFilters, setAppliedFilters] = useState({ voorWie: [], wat: [] });
  const [headingVisible, setHeadingVisible] = useState(false);
  const headingRef = useRef(null);
  const data = siteData.projectsPage;

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setHeadingVisible(true); },
      { threshold: 0.3 }
    );
    if (headingRef.current) observer.observe(headingRef.current);
    return () => observer.disconnect();
  }, []);

  const toggleFilter = (type, value) => {
    setActiveFilters((prev) => ({
      ...prev,
      [type]: prev[type].includes(value)
        ? prev[type].filter((v) => v !== value)
        : [...prev[type], value],
    }));
  };

  const applyFilters = () => {
    setAppliedFilters({ ...activeFilters });
    setFilterOpen(false);
  };

  const clearFilters = () => {
    setActiveFilters({ voorWie: [], wat: [] });
    setAppliedFilters({ voorWie: [], wat: [] });
    setFilterOpen(false);
  };

  const filteredProjects = useMemo(() => {
    return data.allProjects.filter((project) => {
      const matchCategory = appliedFilters.voorWie.length === 0 ||
        project.categories.some((c) => appliedFilters.voorWie.includes(c));
      const matchType = appliedFilters.wat.length === 0 ||
        project.types.some((tp) => appliedFilters.wat.includes(tp));
      return matchCategory && matchType;
    });
  }, [appliedFilters, data.allProjects]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const hasActiveFilters = appliedFilters.voorWie.length > 0 || appliedFilters.wat.length > 0;

  return (
    <div className="min-h-screen" style={{ background: '#0a0a0a' }}>
      {/* Noise texture */}
      <div
        className="fixed inset-0 opacity-30 pointer-events-none z-0"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 512 512' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.5'/%3E%3C/svg%3E")`,
          backgroundSize: '256px 256px',
        }}
      />

      {/* Hero Section */}
      <section className="relative z-10 pt-28 md:pt-36 pb-16 md:pb-20 px-8 md:px-16 lg:px-24">
        {/* Top area - subtitles + logo */}
        <div className="flex items-start justify-between mb-16">
          <div className="space-y-0">
            {siteData.company.subtitles.map((sub, i) => (
              <p key={i} className="text-xs md:text-sm text-gray-500 tracking-[0.15em]">{sub}</p>
            ))}
          </div>
          <div className="hidden lg:block">
            <svg viewBox="0 0 140 80" className="w-28" fill="none">
              <circle cx="70" cy="36" r="33" stroke="white" strokeWidth="2" fill="none" />
              <path d="M95 8 Q106 0, 100 14" stroke="white" strokeWidth="2" fill="none" strokeLinecap="round" />
              <text x="38" y="42" fill="white" fontSize="17" fontWeight="700" fontFamily="Space Grotesk, sans-serif">cobras</text>
            </svg>
          </div>
        </div>

        {/* Heading + Description */}
        <div
          ref={headingRef}
          style={{
            opacity: headingVisible ? 1 : 0,
            transform: headingVisible ? 'translateY(0)' : 'translateY(40px)',
            transition: 'all 0.8s cubic-bezier(0.16, 1, 0.3, 1)',
          }}
        >
          <h1 className="text-5xl md:text-7xl lg:text-8xl font-black text-white uppercase tracking-tight mb-8">
            {get(t.projectsPage.heading)}
          </h1>
          <p className="text-gray-400 text-base md:text-lg leading-relaxed max-w-3xl">
            {get(t.projectsPage.description)}
          </p>
        </div>

        {/* Active Filters Display */}
        {hasActiveFilters && (
          <div className="mt-8 flex flex-wrap gap-2 items-center">
            <span className="text-sm text-gray-500 mr-2">{get(t.projectsPage.filters)}:</span>
            {[...appliedFilters.voorWie, ...appliedFilters.wat].map((f) => (
              <span key={f} className="px-3 py-1 text-xs font-bold text-white rounded-full" style={{ backgroundColor: '#A855F7' }}>
                {f}
              </span>
            ))}
            <button onClick={clearFilters} className="text-sm text-gray-400 hover:text-white ml-2 transition-colors">
              {get(t.projectsPage.clearAll)}
            </button>
          </div>
        )}
      </section>

      {/* Projects List */}
      <section className="relative z-10 px-8 md:px-16 lg:px-24 pb-16">
        <div className="divide-y divide-gray-800/50">
          {filteredProjects.map((project, index) => (
            <ProjectCard key={project.id} project={project} index={index} viewLabel={get(t.projectsPage.view)} />
          ))}
        </div>

        {filteredProjects.length === 0 && (
          <div className="text-center py-24">
            <p className="text-2xl text-gray-500">{get(t.projectsPage.noResults)}</p>
            <button onClick={clearFilters} className="mt-4 text-[#A855F7] font-bold hover:underline">
              {get(t.projectsPage.clearAll)}
            </button>
          </div>
        )}
      </section>

      {/* CTA Section */}
      <section className="relative z-10 py-20 md:py-28 px-8 md:px-16 lg:px-24 text-center">
        <div
          className="h-[1px] mb-16 mx-auto max-w-xl"
          style={{ background: 'linear-gradient(90deg, transparent, rgba(168, 85, 247, 0.3), transparent)' }}
        />
        <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-white italic mb-10 max-w-2xl mx-auto">
          {get(t.projectsPage.ctaHeading)}
        </h2>
        <button
          onClick={() => { navigate('/contact'); window.scrollTo({top:0}); }}
          className="inline-flex items-center gap-3 border-2 text-white px-10 py-4 text-sm font-bold tracking-[0.2em] uppercase transition-all duration-500 group"
          style={{ borderColor: '#A855F7' }}
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#A855F7'; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
        >
          {get(t.cta.button)}
          <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
        </button>
      </section>

      {/* Fixed Bottom Bar */}
      <div className="fixed bottom-6 right-6 md:right-10 z-[60] flex items-center gap-3">
        <button
          onClick={scrollToTop}
          className="flex items-center gap-2 px-5 py-3 text-xs font-bold tracking-[0.15em] uppercase text-white border border-gray-600 bg-[#0a0a0a]/90 backdrop-blur-sm hover:border-white transition-all duration-300"
        >
          {get(t.projectsPage.backToTop)}
          <ArrowUp size={14} />
        </button>
        <button
          onClick={() => { setActiveFilters({...appliedFilters}); setFilterOpen(true); }}
          className="flex items-center gap-2 px-5 py-3 text-xs font-bold tracking-[0.15em] uppercase text-white transition-all duration-300"
          style={{ backgroundColor: '#A855F7' }}
        >
          <SlidersHorizontal size={14} />
          {get(t.projectsPage.filters)}
        </button>
      </div>

      {/* Filter Panel */}
      <FilterPanel
        isOpen={filterOpen}
        onClose={() => setFilterOpen(false)}
        filters={data.filters}
        activeFilters={activeFilters}
        onToggleFilter={toggleFilter}
        onApply={applyFilters}
        onClear={clearFilters}
        t={t}
        get={get}
      />
    </div>
  );
};

export default ProjectsPage;
